import type { Node as PmNode } from "prosemirror-model";
import type { EditorView } from "prosemirror-view";
import { Selection } from "prosemirror-state";
import { GapCursor } from "prosemirror-gapcursor";
import type { Document, Msg } from "../types";
import { titleBlockIndex } from "../card-title";
import { UNREADABLE_TEXT, cardContent, cardError, watchCard } from "./cards";
import { cardMenuButton } from "./card-link-menu";
import { element, iconButton, setIcon } from "./dom";
import { registerSection } from "./sections";
import type { SectionKeys } from "./sections";

// The editor of a live section. Its view is null until the card loads.
export interface SectionEditor {
  readonly view: EditorView | null;
  setEditable(editable: boolean): void;
  destroy(): void;
}

// Makes the editor of a card in `el`.
type OpenEditor = (cardId: string, el: HTMLElement, editable: boolean) => SectionEditor;

// Shows a read-only preview of `content` in `el`.
export type OpenPreview = (el: HTMLElement, content: Document) => { destroy(): void };

// A live section is in an editor of a card. `path` holds the cards from the
// main card to the card of that editor. A preview section is in a read-only
// preview.
type SectionPlace = { openPreview: OpenPreview } & (
  | { kind: "live"; path: string[]; openEditor: OpenEditor }
  | { kind: "preview" }
);

// What the body of a section shows. The body is the same in both states: a
// collapsed section only hides all blocks except the title block (see
// title-line.ts). Only a live place can open an editor, so the editor spec
// holds the function.
type BodySpec =
  | { kind: "editor"; openEditor: OpenEditor }
  | { kind: "preview"; content: Document }
  | { kind: "error"; error: string }
  | { kind: "text"; text: string };

type Body = { spec: BodySpec; editor: SectionEditor | null; destroy(): void };

// The smallest height that a drag can give a section, in pixels.
const MIN_HEIGHT = 40;

// A `card_embed` node: another card, shown as a section. The arrow shows the
// title block only, or the full card. In a live section, the body is an editor
// of that card, so the user changes it on the page of the outer card.
//
// The node is not node-selectable (see schema). A range selection that covers
// it gets the `.block-selected` class from `blockSelectionPlugin`.
export class CardSectionView implements SectionKeys {
  dom: HTMLElement;
  private uuid: string;
  private collapsed: boolean;
  // The height in pixels, or null when the section grows to fit the card.
  private height: number | null;
  // A section is open when it is not collapsed and not blocked. A blocked
  // section cannot open: the card does not exist, or it is open above.
  private open = false;
  private blocked = false;
  private toggleEl: HTMLButtonElement;
  private bodyEl: HTMLElement;
  private body: Body | null = null;
  // Only a live section has a lock.
  private lockEl: HTMLButtonElement | null = null;
  private unwatch: () => void;

  constructor(
    node: PmNode,
    private view: EditorView,
    private getPos: () => number | undefined,
    dispatch: (msg: Msg) => void,
    private place: SectionPlace
  ) {
    this.uuid = node.attrs["uuid"] as string;
    this.collapsed = node.attrs["collapsed"] as boolean;
    this.height = node.attrs["height"] as number | null;

    this.dom = element("div", `card-section card-section--${place.kind}`);
    this.dom.contentEditable = "false";
    this.dom.setAttribute("data-card-section", "");
    this.toggleEl = iconButton("collapsed", "", () => this.setAttrs({ collapsed: !this.collapsed }));
    this.toggleEl.classList.add("card-section__toggle");
    this.bodyEl = element("div", "card-section__body");
    const main = element("div", "card-section__main");
    main.append(this.bodyEl);
    this.dom.append(this.toggleEl, main);

    if (place.kind === "live") {
      // A press on a collapsed section opens it. The capture phase keeps the
      // press away from the read-only editor, so a link or a todo marker in
      // the title block does not act.
      main.addEventListener(
        "mousedown",
        (e) => {
          if (this.open || this.blocked) return;
          e.preventDefault();
          e.stopPropagation();
          this.setAttrs({ collapsed: false });
        },
        true
      );
      const open = iconButton("open", "Open the card", () => dispatch({ type: "NavigateTo", id: this.uuid }));
      const tools = element("div", "card-section__tools");
      tools.append(open, cardMenuButton({ view, getPos, uuid: this.uuid, current: "section" }));
      this.lockEl = iconButton("locked", "", () => this.toggleLock());
      this.lockEl.classList.add("card-section__lock");
      const grip = element("div", "card-section__resize");
      grip.addEventListener("pointerdown", (e) => this.startResize(grip, e));
      this.dom.append(tools, this.lockEl, grip);
      registerSection(this.dom, this);
    }

    this.unwatch = watchCard(this.uuid, dispatch, () => this.render());
  }

  update(node: PmNode): boolean {
    if (node.type.name !== "card_embed" || node.attrs["uuid"] !== this.uuid) return false;
    this.collapsed = node.attrs["collapsed"] as boolean;
    this.height = node.attrs["height"] as number | null;
    this.render();
    return true;
  }

  // The section and its editor handle their own events and DOM changes.
  stopEvent(): boolean {
    return true;
  }

  ignoreMutation(): boolean {
    return true;
  }

  destroy(): void {
    this.unwatch();
    this.body?.destroy();
  }

  enter(forward: boolean): boolean {
    const view = this.body?.editor?.view;
    if (!view || !this.open) return false;
    const doc = view.state.doc;
    view.focus();
    view.dispatch(view.state.tr.setSelection(forward ? Selection.atStart(doc) : Selection.atEnd(doc)).scrollIntoView());
    return true;
  }

  exit(forward: boolean): void {
    const pos = this.getPos();
    if (pos == null) return;
    const { state } = this.view;
    const target = forward ? pos + state.doc.nodeAt(pos)!.nodeSize : pos;
    this.view.focus();
    this.view.dispatch(state.tr.setSelection(new GapCursor(state.doc.resolve(target))).scrollIntoView());
  }

  private render(): void {
    const content = cardContent(this.uuid);
    // A card that contains itself would open without end.
    const cycle = this.place.kind === "live" && this.place.path.includes(this.uuid);
    this.blocked = content === null || cycle;
    this.open = !this.collapsed && !this.blocked;

    this.dom.classList.toggle("card-section--open", this.open);
    this.dom.classList.toggle("card-section--blocked", this.blocked);
    this.dom.classList.toggle("card-section--unlocked", this.open && this.height != null);
    this.dom.classList.toggle("card-section--untitled", !!content && titleBlockIndex(content) < 0);
    this.toggleEl.disabled = this.blocked || this.place.kind === "preview";
    setIcon(
      this.toggleEl,
      this.open ? "expanded" : "collapsed",
      cycle ? "This card is open above" : this.open ? "Show the title only" : "Show the full card"
    );
    this.bodyEl.style.height = this.open && this.height != null ? `${this.height}px` : "";
    if (this.lockEl) {
      if (this.height == null) setIcon(this.lockEl, "locked", "Set the height by hand");
      else setIcon(this.lockEl, "unlocked", "Fit the height to the card");
    }
    this.renderBody(content, cycle);
  }

  private renderBody(content: Document | null | undefined, cycle: boolean): void {
    const spec = this.nextBody(content, cycle);
    if (!this.body || !sameSpec(this.body.spec, spec)) {
      this.body?.destroy();
      this.bodyEl.textContent = "";
      this.body = this.makeBody(spec);
    }
    this.body.editor?.setEditable(this.open);
  }

  // A live section shows an editor of the card. The editor gets the state of
  // the card from the Model, and it shows the card when the state arrives. A
  // card that the editor cannot read gets an error box. A preview section shows
  // a read-only preview. A card that is open above also shows a preview.
  private nextBody(content: Document | null | undefined, cycle: boolean): BodySpec {
    if (content === null) return { kind: "text", text: "Not Found" };
    if (this.place.kind === "live" && !cycle) {
      const error = cardError(this.uuid);
      return error === undefined ? { kind: "editor", openEditor: this.place.openEditor } : { kind: "error", error };
    }
    // An empty body shows "…" (see the CSS), as an editor that waits for its card.
    return content ? { kind: "preview", content } : { kind: "text", text: "" };
  }

  private makeBody(spec: BodySpec): Body {
    const none = () => {};
    switch (spec.kind) {
      case "editor": {
        const editor = spec.openEditor(this.uuid, this.bodyEl, this.open);
        return { spec, editor, destroy: () => editor.destroy() };
      }
      case "preview": {
        const inner = element("div", "");
        this.bodyEl.appendChild(inner);
        const preview = this.place.openPreview(inner, spec.content);
        return { spec, editor: null, destroy: () => preview.destroy() };
      }
      case "error": {
        const box = element("div", "card-unreadable");
        const text = element("p", "");
        text.textContent = UNREADABLE_TEXT;
        const reason = element("code", "");
        reason.textContent = spec.error;
        box.append(text, reason);
        this.bodyEl.appendChild(box);
        return { spec, editor: null, destroy: none };
      }
      case "text":
        this.bodyEl.textContent = spec.text;
        return { spec, editor: null, destroy: none };
    }
  }

  // An unlock keeps the current height, so the section does not jump.
  private toggleLock(): void {
    this.setAttrs({ height: this.height == null ? Math.max(MIN_HEIGHT, this.bodyEl.offsetHeight) : null });
  }

  // Drag the bottom edge of an unlocked section. The height goes into the node
  // when the drag ends, so one drag is one change of the outer card.
  private startResize(grip: HTMLElement, start: PointerEvent): void {
    start.preventDefault();
    grip.setPointerCapture(start.pointerId);
    const startHeight = this.bodyEl.offsetHeight;
    const heightAt = (e: PointerEvent) => Math.max(MIN_HEIGHT, Math.round(startHeight + e.clientY - start.clientY));
    const move = (e: PointerEvent) => {
      this.bodyEl.style.height = `${heightAt(e)}px`;
    };
    const end = (e: PointerEvent) => {
      grip.removeEventListener("pointermove", move);
      grip.removeEventListener("pointerup", end);
      grip.removeEventListener("pointercancel", end);
      this.setAttrs({ height: heightAt(e) });
    };
    grip.addEventListener("pointermove", move);
    grip.addEventListener("pointerup", end);
    grip.addEventListener("pointercancel", end);
  }

  // Store the display of the section in the outer card.
  private setAttrs(attrs: { collapsed?: boolean; height?: number | null }): void {
    const pos = this.getPos();
    const node = pos == null ? null : this.view.state.doc.nodeAt(pos);
    if (pos == null || !node) return;
    this.view.dispatch(this.view.state.tr.setNodeMarkup(pos, null, { ...node.attrs, ...attrs }));
  }
}

function sameSpec(a: BodySpec, b: BodySpec): boolean {
  switch (a.kind) {
    case "editor":
      return b.kind === "editor";
    case "preview":
      return b.kind === "preview" && b.content === a.content;
    case "error":
      return b.kind === "error" && b.error === a.error;
    case "text":
      return b.kind === "text" && b.text === a.text;
  }
}
